import { useState } from 'react'
import { serialFromPayload } from '../serialFromPayload'
import { MakerCamera } from './MakerCamera'

/**
 * The makers' own serials, one after another — scanned with the camera, fired
 * in by a USB scanner (which types and presses Enter), or typed.
 *
 * Silent on success, like the intake loop: the list grows and the counter
 * moves, which is all the feedback somebody with a box in each hand can use.
 * Only a problem gets a sentence — the same box twice, or a Kronos label,
 * which is our number, not the maker's.
 */
export function MakerSerials({
  serials,
  expected,
  onChange,
}: {
  serials: readonly string[]
  expected: number
  onChange: (next: readonly string[]) => void
}): JSX.Element {
  const [text, setText] = useState('')
  const [camera, setCamera] = useState(false)
  const [problem, setProblem] = useState<string | null>(null)

  const add = (raw: string): void => {
    const value = raw.trim()
    if (value === '') return
    if (/^https?:\/\//i.test(value) && serialFromPayload(value)) {
      setProblem('That is a Kronos label, not the maker’s serial. Scan the number printed on the box or the part.')
      return
    }
    if (serials.some((s) => s.toUpperCase() === value.toUpperCase())) {
      setProblem(`${value} is already in the list.`)
      return
    }
    setProblem(null)
    onChange([...serials, value])
  }

  return (
    <div className="inv-makers">
      <form
        className="inv-makers__entry"
        onSubmit={(e) => {
          e.preventDefault()
          add(text)
          setText('') // cleared at once, so a USB scanner can fire the next
        }}
      >
        <input
          type="text"
          className="inv-serial-input"
          value={text}
          onChange={(e) => setText(e.target.value)}
          placeholder="Scan or type"
          aria-label="Maker’s serial"
          autoComplete="off"
          autoCapitalize="characters"
          spellCheck={false}
          enterKeyHint="next"
        />
        <button type="submit" disabled={text.trim() === ''}>
          Add
        </button>
        <button type="button" onClick={() => setCamera((c) => !c)} aria-pressed={camera}>
          {camera ? 'Close camera' : 'Camera'}
        </button>
      </form>

      {camera ? <MakerCamera onScan={add} /> : null}
      {problem ? <p className="critical">{problem}</p> : null}

      {serials.length > 0 ? (
        <div className="panel">
          <div className="inv-makers__count">
            <span className="figure">{serials.length}</span> of {Math.max(expected, serials.length)} have a maker’s serial
          </div>
          <ol className="row-list inv-makers__list">
            {[...serials].reverse().map((s, i) => (
              <li key={s} className="row-item">
                <span className="row-item__main">
                  <span className="serial">{s}</span>
                </span>
                <span className="row-item__side">
                  <span>#{serials.length - i}</span>
                  <button
                    type="button"
                    className="inv-remove"
                    aria-label={`Remove ${s}`}
                    onClick={() => onChange(serials.filter((x) => x !== s))}
                  >
                    ✕
                  </button>
                </span>
              </li>
            ))}
          </ol>
        </div>
      ) : null}
    </div>
  )
}
